import * as React from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Loader2, Smile } from 'lucide-react';
import EmojiPicker, {
  EmojiStyle,
  Theme as EmojiTheme,
  type EmojiClickData,
} from 'emoji-picker-react';
import { useTheme } from '@proptr/ui';
import { uploadImage } from '../lib/uploadImage.js';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';
import { Label } from './ui/label.js';

// Combined emoji + image picker. Setting an emoji clears the image and
// vice versa, so the rendered button has a single icon source.

export function IconOrImagePicker({
  icon,
  image,
  onIconChange,
  onImageChange,
  // The proptree this image belongs to. Used by the upload endpoint
  // to bake the right `x-amz-meta-proptree-id` into the signature so
  // the resulting object on DO Spaces can be traced back to its
  // owning doc without a DB lookup.
  proptreeId,
  label = 'Icon or image (optional)',
}: {
  icon: string | null;
  image: string | null;
  onIconChange: (icon: string | null) => void;
  onImageChange: (url: string | null) => void;
  proptreeId: string;
  label?: string;
}) {
  const [tab, setTab] = React.useState<'emoji' | 'image'>(
    image ? 'image' : 'emoji',
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        <div className="inline-flex shrink-0 rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
          <TabButton
            active={tab === 'emoji'}
            onClick={() => setTab('emoji')}
          >
            Emoji
          </TabButton>
          <TabButton
            active={tab === 'image'}
            onClick={() => setTab('image')}
          >
            Image
          </TabButton>
        </div>
      </div>
      <div className="mt-2">
        {tab === 'emoji' ? (
          <EmojiPickerField value={icon} onChange={onIconChange} />
        ) : (
          <ImagePicker
            value={image}
            onChange={onImageChange}
            proptreeId={proptreeId}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function EmojiPickerField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (icon: string | null) => void;
}) {
  const { theme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{
    left: number;
    top: number;
    placeAbove: boolean;
  } | null>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const PICKER_H = 380;
      const margin = 8;
      const spaceBelow = window.innerHeight - r.bottom;
      const placeAbove = spaceBelow < PICKER_H + margin;
      setPos({
        left: r.left,
        top: placeAbove ? r.top - margin : r.bottom + margin,
        placeAbove,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-[22px] leading-none">
        {value ? value : <Smile className="h-4 w-4 text-muted-foreground" />}
      </div>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      >
        <Smile />
        {value ? 'Change emoji' : 'Pick emoji'}
      </Button>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
        >
          Remove
        </Button>
      )}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            className="z-[60]"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              transform: pos.placeAbove ? 'translateY(-100%)' : undefined,
            }}
          >
            <EmojiPicker
              theme={theme === 'dark' ? EmojiTheme.DARK : EmojiTheme.LIGHT}
              emojiStyle={EmojiStyle.NATIVE}
              searchPlaceHolder="Search emoji…"
              width={340}
              height={380}
              previewConfig={{ showPreview: false }}
              onEmojiClick={(data: EmojiClickData) => {
                onChange(data.emoji);
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

function ImagePicker({
  value,
  onChange,
  proptreeId,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  proptreeId: string;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      onChange(
        await uploadImage({ file, kind: 'proptree-button', proptreeId }),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {busy ? <Loader2 className="animate-spin" /> : <ImageIcon />}
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            disabled={busy}
          >
            Remove
          </Button>
        )}
      </div>
      {error && (
        <p className="mt-2 font-sans text-[12px] text-destructive">{error}</p>
      )}
    </div>
  );
}
